import React, { useState, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { ChevronUp } from "lucide-react";
import { MoodCard } from "./MoodCard";
import { getLane } from "../lib/moodLanes";

const STYLE_EDGE = {
    bold: "mc-edge-bold",
    poetic: "mc-edge-poetic",
    relatable: "mc-edge-relatable",
};

// Default emblem when no lane is known — keeps the existing italic MC monogram.
const DEFAULT_EMBLEM = { glyph: "MC", color: "#7C1B26", isMonogram: true };

function emblemForLane(laneKey) {
    if (!laneKey) return DEFAULT_EMBLEM;
    const l = getLane(laneKey);
    if (!l) return DEFAULT_EMBLEM;
    return { glyph: l.emblem || l.glyph || "MC", color: l.emblemColor || "#7C1B26", isMonogram: false };
}

/**
 * Immersive "Pull-From-Sleeve" interactive card.
 *
 * States:
 *   - peek (default)  → card visible only at top of sleeve, drag-up or tap to open
 *   - opened          → card fully out, 3D tilt, light sweep crosses face once,
 *                       then settles with a subtle breathing motion.
 *
 * Interaction:
 *   - tap "Open Card" button
 *   - tap the card itself
 *   - swipe upward on the card (Framer Motion drag, settle threshold ≈ 80px)
 */
export default function MoodCardSleeve({ card, showWatermark = true, onOpened, lane = null, temperature = null, moodId = null, fill = false }) {
    const [opened, setOpened] = useState(false);
    const [sheenShown, setSheenShown] = useState(false);
    const sheenTimer = useRef(null);
    const dragY = useMotionValue(0);
    const emblem = emblemForLane(lane);

    // While dragging, lean the card slightly forward (3D feel)
    const dragRotate = useTransform(dragY, [-200, 0, 60], [-6, 0, 4]);
    const dragScale = useTransform(dragY, [-200, 0], [1.025, 1]);

    const handleOpen = () => {
        if (opened) return;
        setOpened(true);
        setSheenShown(true);
        // Tactile haptic feedback (iOS/Android) — short pulse that mirrors
        // the spring lift. navigator.vibrate is a no-op on desktop / unsupported.
        try {
            if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
                navigator.vibrate([12, 20, 8]);
            }
        } catch { /* ignore */ }
        // Hide sheen layer after one sweep (it auto-removes via CSS animation duration)
        if (sheenTimer.current) clearTimeout(sheenTimer.current);
        sheenTimer.current = setTimeout(() => setSheenShown(false), 1500);
        onOpened?.();
    };

    const handleDragEnd = (_, info) => {
        if (!opened && info.offset.y < -70) {
            handleOpen();
        }
    };

    const edgeClass = STYLE_EDGE[card?.style] || "";

    return (
        <div className={fill ? "relative w-full h-full flex items-center justify-center" : "relative w-full"} data-testid="mood-card-sleeve">
            {/* iter-100 — top instruction. The chevron-up was confusing
                (looked like an "up arrow" some users tried to tap). Replaced
                with an explicit invitation: "tap the seal to open ⌄". The
                glyph itself does the heavy emotional lifting now. */}
            <AnimatePresence>
                {!opened && (
                    <motion.div
                        key="hint"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.7, delay: 0.6 }}
                        className="absolute -top-7 left-0 right-0 flex flex-col items-center pointer-events-none z-20"
                    >
                        <span className="font-serif italic text-[13px] text-app-2 tracking-wide">
                            tap the seal to open
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Sleeve container — sized to hold the card with proportional peek.
                iter-91 — on mobile the card now stretches to 95% of viewport
                width (capped at 460px so larger phones still feel composed)
                and adds a strong floating drop-shadow so it reads as a
                premium object placed on the dark backdrop, not a panel
                pinned to the page. Desktop unchanged (maxWidth 360).
                iter-92 — `fill` mode (used by PublicCard mobile) flips
                the sizing strategy so the card grows to fill its parent's
                allocated height while keeping the 9:16 aspect ratio. The
                parent provides a height-bounded flex slot; we derive width
                from height so the card occupies the maximum possible
                vertical space without forcing the page to scroll. */}
            <div
                className="relative mc-sleeve mx-auto"
                style={fill ? {
                    aspectRatio: "9 / 16",
                    height: "100%",
                    width: "auto",
                    maxWidth: "100%",
                    maxHeight: "100%",
                    filter: "drop-shadow(0 24px 60px rgba(0,0,0,0.65)) drop-shadow(0 -8px 24px rgba(232,199,112,0.10))",
                    perspective: 1400,
                    perspectiveOrigin: "50% 30%",
                } : {
                    aspectRatio: "9 / 16",
                    width: "100%",
                    maxWidth: "min(95vw, 460px)",
                    filter: "drop-shadow(0 24px 60px rgba(0,0,0,0.65)) drop-shadow(0 -8px 24px rgba(232,199,112,0.10))",
                    perspective: 1400,
                    perspectiveOrigin: "50% 30%",
                }}
            >
                {/* Sleeve lip (front pocket) — slides DOWN and fades when card opens */}
                <motion.div
                    className="mc-sleeve-lip"
                    animate={opened
                        ? { y: "60%", opacity: 0 }
                        : { y: "0%", opacity: 1 }}
                    transition={{ type: "spring", stiffness: 110, damping: 22, mass: 1.1, delay: opened ? 0 : 0.15 }}
                >
                    {/* iter-100 — sound-wave ripples around the wax seal.
                        Layered behind so they read as "the seal is broadcasting".
                        Two rings staggered for a continuous radar effect. Hidden
                        once opened. */}
                    {!opened && (
                        <>
                            <span className="mc-wax-ripple" aria-hidden="true" />
                            <span className="mc-wax-ripple delay-1" aria-hidden="true" />
                        </>
                    )}

                    {/* Wax-seal sticker — adaptive emblem + accent color per lane.
                        iter-100 — now the PRIMARY tap target on the unopened
                        sleeve. Heart-beating + amber-haloed + tappable. */}
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); if (!opened) handleOpen(); }}
                        className="mc-wax-seal"
                        aria-label="tap to open"
                        data-testid="wax-seal-button"
                        style={{
                            background: `radial-gradient(60% 60% at 35% 30%, rgba(255,255,255,0.18) 0%, transparent 60%), radial-gradient(120% 120% at 50% 30%, ${emblem.color}EE 0%, ${emblem.color}AA 65%, ${emblem.color}66 100%)`,
                            pointerEvents: opened ? "none" : "auto",
                            border: "none",
                            cursor: opened ? "default" : "pointer",
                        }}
                    >
                        <span
                            className={emblem.isMonogram ? "mc-wax-seal-monogram" : "mc-wax-seal-glyph"}
                            data-testid="wax-seal-emblem"
                        >
                            {emblem.glyph}
                        </span>
                    </button>
                    <span className="mc-sleeve-mark">MOODCARD</span>
                </motion.div>

                {/* Card — Framer Motion drag + animated position.
                    Initially z-2 (BEHIND lip → looks tucked into sleeve).
                    When opened, animates to z-10 (above lip), translates upward
                    leaving the empty sleeve behind.
                    dragElastic is low (0.08) → resistance feels like pulling
                    paper from an envelope rather than a frictionless slider. */}
                <motion.div
                    className="absolute inset-0"
                    style={{
                        y: dragY,
                        rotateX: opened ? 0 : dragRotate,
                        scale: opened ? 1 : dragScale,
                        transformStyle: "preserve-3d",
                        cursor: opened ? "default" : "pointer",
                        zIndex: opened ? 10 : 2,
                    }}
                    animate={
                        opened
                            ? { y: fill ? "-4%" : "-12%", rotateX: -6, scale: fill ? 1.0 : 1.05 }
                            : { y: 0 }
                    }
                    transition={{
                        type: "spring",
                        stiffness: 95,           // gentler than 110 — more deliberate
                        damping: 24,             // settles cleanly, no bounce overshoot
                        mass: 1.25,              // feels weightier, more cinematic
                        restDelta: 0.0005,
                    }}
                    whileTap={!opened ? { scale: 0.985 } : {}}
                    onClick={() => !opened && handleOpen()}
                    data-testid="sleeve-card-wrapper"
                >
                    {/* Wrap MoodCard so we can apply style-specific glow without
                        touching the core MoodCard component. */}
                    <div className={`relative w-full rounded-2xl ${edgeClass} ${opened ? "mc-breathing" : ""}`}>
                        <MoodCard card={card} moodId={moodId} showWatermark={showWatermark} lane={lane} temperature={temperature} />
                        {/* Glossy light sweep — only renders once after opening */}
                        {sheenShown && <div className="mc-sheen-layer" />}
                    </div>
                </motion.div>
            </div>

            {/* Open Card CTA — only before opened. Hidden in fill mode
                because the card itself accepts tap and the dock provides
                the primary affordance below. */}
            <AnimatePresence>
                {!opened && !fill && (
                    <motion.button
                        key="open-cta"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        onClick={handleOpen}
                        data-testid="sleeve-open-button"
                        className="mt-6 w-full py-3.5 rounded-full font-serif italic text-[15px] tracking-wide btn-metal"
                    >
                        open gently
                    </motion.button>
                )}
            </AnimatePresence>
        </div>
    );
}
